// Ship's Papers — the Vessel & crew tab: vessel details, who's aboard, invitations, audit trail.
// Single-owner boats see a name and an "Invite someone" button — nothing else.
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlusIcon, PencilIcon, ClockIcon } from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { useToastStore } from '../../store/toastStore';
import type { VesselDetail, Role, Member } from '../../types/papers';
import { ROLE_LABEL, ROLE_RANK } from '../../types/papers';
import { btnPrimary, btnGhost, btnDanger, input, label, errMsg, fmtBytes, fmtAgo, fmtDate, Modal } from './shared';
import { ScoutPinCard } from './ScoutPin';

const ROLE_HELP: Record<Exclude<Role, 'owner'>, string> = {
  captain: 'Everything the owner can do, except appoint captains.',
  officer: 'Adds and manages the vessel’s documents; sees everything marked for officers.',
  crew: 'Reads what is shared with everyone aboard; keeps their own papers.',
  guest: 'Read-only, for a surveyor or agent — access ends on a date you choose.',
};

export function VesselCrewPanel({ vid, detail, onChanged }: { vid: string; detail: VesselDetail; onChanged: () => void }) {
  const qc = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);
  const me = detail.me;
  const canManage = ROLE_RANK[me.role] >= ROLE_RANK.captain;
  const isOwner = me.role === 'owner';
  const [inviting, setInviting] = useState(false);
  const [editingVessel, setEditingVessel] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Remove, leave or cancel an invitation — each asks first. */
  const [confirming, setConfirming] = useState<{ m: Member; kind: 'remove' | 'leave' | 'uninvite' } | null>(null);

  const refresh = () => { qc.invalidateQueries({ queryKey: ['papers-vessel', vid] }); qc.invalidateQueries({ queryKey: ['papers-access'] }); onChanged(); };
  const member = useMutation({
    mutationFn: ({ mid, patch }: { mid: string; patch: { role?: Role; title?: string | null } }) => api.papersUpdateMember(vid, mid, patch),
    onSuccess: refresh, onError: (e) => setError(errMsg(e, 'Could not change that member.')),
  });
  const remove = useMutation({
    mutationFn: (mid: string) => api.papersRemoveMember(vid, mid),
    onSuccess: () => {
      const kind = confirming?.kind;
      setConfirming(null); refresh();
      addToast({ type: 'success', title: kind === 'leave' ? `You have left ${detail.vessel.name}` : kind === 'uninvite' ? 'Invitation cancelled' : 'Removed from the vessel' });
    },
    onError: (e) => { setConfirming(null); setError(errMsg(e, 'Could not remove that member.')); },
  });
  /** Only the owner changes or removes a captain; a captain manages officers, crew and guests. */
  const canChange = (m: Member) => canManage && m.role !== 'owner' && (isOwner || m.role !== 'captain');
  const nameOf = (m: Member) => m.userName ?? m.userEmail ?? 'this crew member';

  const active = detail.members.filter((m) => m.status === 'active');
  const invited = detail.members.filter((m) => m.status === 'invited');
  const v = detail.vessel;

  return (
    <div className="space-y-5">
      <section className="bg-slate-800/60 border border-slate-700 rounded-2xl p-4">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <h2 className="text-lg font-bold text-white">{v.name}</h2>
            <div className="text-sm text-slate-300 flex flex-wrap gap-x-4 gap-y-1 mt-1">
              {v.mmsi && <span>MMSI {v.mmsi}</span>}{v.callSign && <span>Call sign {v.callSign}</span>}{v.flag && <span>Flag {v.flag}</span>}{v.homePort && <span>Home port {v.homePort}</span>}
              <span>Storage {fmtBytes(detail.storage.usedBytes)} of {fmtBytes(detail.storage.quotaBytes)}</span>
            </div>
          </div>
          {canManage && <button type="button" onClick={() => setEditingVessel(true)} className={btnGhost}><PencilIcon className="w-4 h-4" /> Edit</button>}
        </div>
      </section>

      <section className="bg-slate-800/60 border border-slate-700 rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-base font-bold text-white">Who's aboard</h3>
          <span className="text-sm text-slate-400">{active.length}</span>
          <span className="flex-1" />
          {canManage && <button type="button" onClick={() => setInviting(true)} className={btnPrimary}><UserPlusIcon className="w-4 h-4" /> Invite someone</button>}
        </div>
        <ul className="divide-y divide-slate-700/70">
          {active.map((m) => (
            <li key={m.id} className="flex items-center gap-3 py-2.5">
              <div className="flex-1 min-w-0">
                <div className="text-base font-semibold text-white truncate">{m.userName ?? m.userEmail ?? 'Crew member'}{m.userId === me.userId && <span className="text-slate-400 font-normal"> (you)</span>}</div>
                <div className="text-sm text-slate-400 truncate">{m.title ? `${m.title} · ` : ''}{canManage && m.userEmail ? m.userEmail : ''}{m.expiresAt ? ` · access until ${fmtDate(m.expiresAt)}` : ''}</div>
              </div>
              {canChange(m) ? (
                <select aria-label={`Role for ${m.userName ?? 'member'}`} value={m.role} onChange={(e) => member.mutate({ mid: m.id, patch: { role: e.target.value as Role } })} className="rounded-lg bg-slate-900 border border-slate-600 text-white text-sm px-2 py-1.5">
                  {(['captain', 'officer', 'crew', 'guest'] as Role[]).filter((r) => r !== 'captain' || isOwner).map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                </select>
              ) : <span className="text-sm font-semibold text-amber-300">{ROLE_LABEL[m.role]}</span>}
              {canChange(m) && <button type="button" onClick={() => setConfirming({ m, kind: 'remove' })} className={btnDanger}>Remove</button>}
              {!canManage && m.userId === me.userId && m.role !== 'owner' && <button type="button" onClick={() => setConfirming({ m, kind: 'leave' })} className={btnGhost}>Leave</button>}
            </li>
          ))}
        </ul>
        {canManage && invited.length > 0 && (<>
          <h4 className="text-sm font-semibold text-slate-300 mt-4 mb-1">Invited, not yet aboard</h4>
          <ul className="divide-y divide-slate-700/70">
            {invited.map((m) => (
              <li key={m.id} className="flex items-center gap-3 py-2 text-sm">
                <div className="flex-1 min-w-0 text-slate-200 truncate">{m.invitedEmail} · {ROLE_LABEL[m.role]}{m.title ? ` · ${m.title}` : ''} <span className="text-slate-500">invited {fmtAgo(m.createdAt)}</span></div>
                <button type="button" onClick={() => setConfirming({ m, kind: 'uninvite' })} className={btnGhost}>Cancel invitation</button>
              </li>
            ))}
          </ul>
        </>)}
        {error && <p role="alert" className="text-sm font-medium text-red-300 mt-2">{error}</p>}
      </section>

      <ScoutPinCard vid={vid} />

      {canManage && (
        <section className="bg-slate-800/60 border border-slate-700 rounded-2xl p-4">
          <button type="button" onClick={() => setShowAudit((s) => !s)} className="inline-flex items-center gap-2 text-base font-bold text-white"><ClockIcon className="w-5 h-5 text-amber-400" /> Activity {showAudit ? '▾' : '▸'}</button>
          {showAudit && <AuditList vid={vid} />}
        </section>
      )}

      {confirming && (() => {
        const { m, kind } = confirming;
        const v = detail.vessel.name;
        const [title, body, action] = kind === 'leave'
          ? [`Leave ${v}?`, `You will no longer see ${v}'s papers. Your own papers stay with you. Only the owner or captain can bring you back aboard.`, `Leave ${v}`]
          : kind === 'uninvite'
            ? [`Cancel the invitation to ${m.invitedEmail}?`, 'The link in their email will stop working. You can invite them again later.', 'Cancel the invitation']
            : [`Remove ${nameOf(m)} from ${v}?`, `They will no longer see ${v}'s papers, and their own papers leave your view. You can invite them again later.`, `Remove from ${v}`];
        return (
          <Modal title={title} onClose={() => !remove.isPending && setConfirming(null)}>
            <p className="text-base text-slate-300">{body}</p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setConfirming(null)} disabled={remove.isPending} className={btnGhost}>{kind === 'uninvite' ? 'Keep it' : 'Cancel'}</button>
              <button type="button" onClick={() => remove.mutate(m.id)} disabled={remove.isPending} className={`${btnPrimary} !bg-red-500 !text-white`}>{remove.isPending ? 'Working…' : action}</button>
            </div>
          </Modal>
        );
      })()}

      {inviting && <InviteModal vid={vid} isOwner={isOwner} onClose={() => setInviting(false)} onDone={() => { setInviting(false); refresh(); }} />}
      {editingVessel && <VesselModal vid={vid} detail={detail} onClose={() => setEditingVessel(false)} onDone={() => { setEditingVessel(false); refresh(); }} />}
    </div>
  );
}

function InviteModal({ vid, isOwner, onClose, onDone }: { vid: string; isOwner: boolean; onClose: () => void; onDone: () => void }) {
  const addToast = useToastStore((s) => s.addToast);
  const [email, setEmail] = useState(''); const [role, setRole] = useState<Role>('crew'); const [title, setTitle] = useState(''); const [until, setUntil] = useState('');
  const [error, setError] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: () => api.papersInvite(vid, { email: email.trim(), role, title: title.trim() || null, expires_at: role === 'guest' && until ? new Date(until + 'T23:59:59').toISOString() : null }),
    onSuccess: (r) => { addToast({ type: 'success', title: 'Invitation sent', message: r.alreadyAccount ? 'They already have a MyPolaris account — one click and they are aboard.' : 'They will be asked to create a MyPolaris login with that email.' }); onDone(); },
    onError: (e) => setError(errMsg(e, 'Could not send the invitation.')),
  });
  return (
    <Modal title="Invite someone aboard" onClose={onClose} dirty={!!(email.trim() || title.trim() || until)}>
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); send.mutate(); }}>
        <label className={label}>Their email<input type="email" className={`${input} mt-1`} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" autoFocus /></label>
        <fieldset><legend className={label}>Role</legend>
          <div className="mt-1 space-y-1.5">
            {(['captain', 'officer', 'crew', 'guest'] as Exclude<Role, 'owner'>[]).filter((r) => r !== 'captain' || isOwner).map((r) => (
              <label key={r} className={`flex items-start gap-3 rounded-lg border px-3 py-2 cursor-pointer ${role === r ? 'border-amber-400 bg-amber-400/5' : 'border-slate-700'}`}>
                <input type="radio" name="role" className="mt-1 w-4 h-4 text-amber-400" checked={role === r} onChange={() => setRole(r)} />
                <span><span className="block text-base font-semibold text-white">{ROLE_LABEL[r]}</span><span className="block text-sm text-slate-400">{ROLE_HELP[r]}</span></span>
              </label>
            ))}
          </div>
        </fieldset>
        <label className={label}>Position aboard <span className="text-slate-400 font-normal">(optional, e.g. Chief Engineer)</span><input className={`${input} mt-1`} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} /></label>
        {role === 'guest' && <label className={label}>Access ends on<input type="date" min={new Date().toISOString().slice(0, 10)} max={new Date(Date.now() + 365 * 86_400_000).toISOString().slice(0, 10)} className={`${input} mt-1`} value={until} onChange={(e) => setUntil(e.target.value)} /></label>}
        {error && <p role="alert" className="text-sm font-medium text-red-300 bg-red-900/30 border border-red-800/60 rounded-lg px-3 py-2">{error}</p>}
        <div className="flex justify-end gap-2"><button type="button" onClick={onClose} className={btnGhost}>Cancel</button><button type="submit" disabled={send.isPending || !email.includes('@')} className={btnPrimary}>{send.isPending ? 'Sending…' : 'Send invitation'}</button></div>
      </form>
    </Modal>
  );
}

function VesselModal({ vid, detail, onClose, onDone }: { vid: string; detail: VesselDetail; onClose: () => void; onDone: () => void }) {
  const v = detail.vessel;
  const [f, setF] = useState({ name: v.name, mmsi: v.mmsi ?? '', call_sign: v.callSign ?? '', flag: v.flag ?? '', home_port: v.homePort ?? '' });
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({ mutationFn: () => api.papersUpdateVessel(vid, { name: f.name, mmsi: f.mmsi || null, call_sign: f.call_sign || null, flag: f.flag || null, home_port: f.home_port || null }), onSuccess: onDone, onError: (e) => setError(errMsg(e, 'Could not save the vessel.')) });
  return (
    <Modal title="Vessel details" onClose={onClose} dirty={JSON.stringify(f) !== JSON.stringify({ name: v.name, mmsi: v.mmsi ?? '', call_sign: v.callSign ?? '', flag: v.flag ?? '', home_port: v.homePort ?? '' })}>
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <label className={label}>Name<input className={`${input} mt-1`} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} maxLength={120} /></label>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className={label}>MMSI<input className={`${input} mt-1`} value={f.mmsi} onChange={(e) => setF({ ...f, mmsi: e.target.value })} maxLength={12} /></label>
          <label className={label}>Call sign<input className={`${input} mt-1`} value={f.call_sign} onChange={(e) => setF({ ...f, call_sign: e.target.value })} maxLength={16} /></label>
          <label className={label}>Flag<input className={`${input} mt-1`} value={f.flag} onChange={(e) => setF({ ...f, flag: e.target.value })} maxLength={64} /></label>
          <label className={label}>Home port<input className={`${input} mt-1`} value={f.home_port} onChange={(e) => setF({ ...f, home_port: e.target.value })} maxLength={120} /></label>
        </div>
        {error && <p role="alert" className="text-sm font-medium text-red-300">{error}</p>}
        <div className="flex justify-end gap-2"><button type="button" onClick={onClose} className={btnGhost}>Cancel</button><button type="submit" disabled={save.isPending || !f.name.trim()} className={btnPrimary}>{save.isPending ? 'Saving…' : 'Save'}</button></div>
      </form>
    </Modal>
  );
}

const ACTION_LABEL: Record<string, string> = {
  viewed: 'viewed', downloaded: 'downloaded', created: 'added', updated: 'edited', file_added: 'added a file to', deleted: 'deleted',
  member_invited: 'invited', member_joined: 'came aboard', member_changed: 'changed a role', member_removed: 'removed', equipment_added: 'added equipment', equipment_removed: 'removed equipment',
  pin_locked: 'locked PIN entry after too many wrong PINs',
};
function AuditList({ vid }: { vid: string }) {
  const { data, isLoading } = useQuery({ queryKey: ['papers-events', vid], queryFn: () => api.papersEvents(vid) });
  if (isLoading) return <p className="text-sm text-slate-400 mt-2">Loading…</p>;
  if (!data?.length) return <p className="text-sm text-slate-400 mt-2">Nothing yet.</p>;
  return (
    <ul className="mt-2 divide-y divide-slate-700/70 text-sm">
      {data.slice(0, 100).map((e) => (
        <li key={e.id} className="py-1.5 flex gap-2"><span className="text-slate-500 w-24 shrink-0">{fmtAgo(e.createdAt)}</span><span className="text-slate-200"><b>{e.action === 'pin_locked' ? 'A Scout' : e.userName ?? 'Someone'}</b> {ACTION_LABEL[e.action] ?? e.action}{e.meta && 'title' in e.meta ? ` “${String(e.meta.title)}”` : ''}{e.meta && 'email' in e.meta ? ` ${String(e.meta.email)}` : ''}</span></li>
      ))}
    </ul>
  );
}
